import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDashboard } from "../api/weddings";
import { DashboardResponse } from "../types";
import { formatDate, formatPrice } from "../utils/format";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";

const Dashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: dashboard } = await getDashboard();
      setData(dashboard);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    load();
  }, [user, authLoading, navigate, load]);

  if (authLoading || loading) return <Loading message="Loading dashboard..." />;
  if (error) return <ErrorMessage message={error} onRetry={load} />;
  if (!data) return null;

  const { totals, listings } = data;

  return (
    <div className="page">
      <h1>My Weddings</h1>
      <div className="golden-thread" />

      <div className="features" style={{ marginBottom: "2.5rem" }}>
        <div className="feature-card">
          <h3>{totals.wedding_count}</h3>
          <p>Weddings</p>
        </div>
        <div className="feature-card">
          <h3>{totals.pending_approvals}</h3>
          <p>Pending Approvals</p>
        </div>
        <div className="feature-card">
          <h3>{formatPrice(totals.revenue_total_cents)}</h3>
          <p>Total Revenue</p>
        </div>
      </div>

      <div className="section-header">
        <h2>Your Listings</h2>
        <Link to="/weddings/create" className="btn btn-primary btn-sm">
          Create Wedding
        </Link>
      </div>

      {listings.length === 0 ? (
        <p className="empty-state">
          No listings yet.{" "}
          <Link to="/weddings/create">Create your first wedding</Link>.
        </p>
      ) : (
        <div className="wedding-cards">
          {listings.map((listing) => (
            <div key={listing.id} className="wedding-card">
              <h3>{listing.title}</h3>
              <div className="wedding-card-meta">
                <span className="wedding-date">{formatDate(listing.date)}</span>
                {(listing.city || listing.venue_name || listing.location) && (
                  <span className="wedding-card-location">
                    {listing.venue_name || listing.location}
                    {listing.city
                      ? ` · ${listing.city}${listing.state ? `, ${listing.state}` : ""}`
                      : ""}
                  </span>
                )}
              </div>

              <div className="wedding-card-tags">
                <span className="badge">
                  {(listing.listing_status || "draft").replace("_", " ")}
                </span>
                <span className="badge">
                  {listing.pending_approvals} pending
                </span>
                <span className="badge">
                  {formatPrice(listing.revenue_total_cents)} revenue
                </span>
              </div>

              <p className="text-muted">
                RSVPs — confirmed {listing.rsvp_stats.confirmed}, pending{" "}
                {listing.rsvp_stats.pending}, declined {listing.rsvp_stats.declined}
              </p>

              <div className="button-row">
                <Link
                  to={`/weddings/${listing.id}/manage`}
                  className="btn btn-primary btn-sm"
                >
                  Manage
                </Link>
                <Link
                  to={`/weddings/${listing.id}/edit`}
                  className="btn btn-secondary btn-sm"
                >
                  Edit Listing
                </Link>
                <Link
                  to={`/weddings/${listing.id}`}
                  className="btn btn-secondary btn-sm"
                >
                  View Public Page
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
